<template>
  <div class="py-4 container-fluid">
    <!-- ======= HEADER ======= -->
    <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
      <div class="text-white">
        <h4 class="text-white mb-0"><i class="fas fa-handshake"></i> Vendor Management</h4>
        <span class="text-sm opacity-8">Data vendor, performa transaksi PO, dan status kelengkapan pajak</span>
      </div>
      <div class="mt-2">
        <a class="btn btn-sm btn-success mb-0 me-2" :href="exportUrl('export-excel/vendor-list')" target="_BLANK"><i class="fas fa-file-excel"></i> Excel</a>
        <a class="btn btn-sm btn-primary mb-0 me-2" :href="exportUrl('print-pdf/vendor-list')" target="_BLANK"><i class="fas fa-file-pdf"></i> PDF</a>
        <argon-button variant="gradient" color="success" size="sm" @click="create()"><i class="fa fa-plus fa-sm"></i> Add Vendor</argon-button>
      </div>
    </div>

    <!-- ======= KPI ======= -->
    <div class="row">
      <div class="col-xl col-md-3 col-6 mb-3" v-for="kpi in kpis" :key="kpi.label">
        <div class="card kpi-card h-100" :class="{ 'kpi-clickable': kpi.action }" @click="kpi.action && kpi.action()">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <p class="text-xxs text-uppercase font-weight-bolder text-secondary mb-1">{{ kpi.label }}</p>
                <h5 class="mb-0 font-weight-bolder">{{ kpi.value }}</h5>
              </div>
              <div class="kpi-icon" :class="'bg-gradient-' + kpi.color"><i :class="kpi.icon"></i></div>
            </div>
            <p class="text-xxs text-secondary mb-0 mt-2">{{ kpi.note }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- ======= KATEGORI ======= -->
      <div class="col-lg-7 mb-3">
        <div class="card h-100">
          <div class="card-header pb-0"><h6 class="mb-0">Sebaran Kategori Vendor</h6><span class="text-xs text-secondary">Klik kategori untuk memfilter tabel</span></div>
          <div class="card-body pt-3">
            <div class="cat-row" v-for="(count, name) in stats.categories" :key="name" :class="{ active: search.vendor_category == name }" @click="toggleCategory(name)">
              <span class="cat-name text-xs font-weight-bold"><span class="cat-dot" :style="{ background: categoryColor(name) }"></span>{{ name }}</span>
              <div class="cat-bar-bg"><div class="cat-bar" :style="{ width: (count / maxCategory * 100) + '%', background: categoryColor(name) }"></div></div>
              <span class="text-xs font-weight-bolder cat-count">{{ count }}</span>
            </div>
          </div>
        </div>
      </div>
      <!-- ======= TOP VENDOR ======= -->
      <div class="col-lg-5 mb-3">
        <div class="card h-100">
          <div class="card-header pb-0"><h6 class="mb-0">Top 5 Vendor (Nilai PO)</h6><span class="text-xs text-secondary">Nilai PO excl. PPN, dalam IDR</span></div>
          <div class="card-body pt-3">
            <div class="d-flex align-items-center mb-3 top-row" v-for="(v, i) in stats.top" :key="v.vendor_code" @click="openDetail(v.vendor_code)">
              <span class="rank" :class="'rank-' + (i + 1)">{{ i + 1 }}</span>
              <div class="flex-grow-1 ms-2" style="min-width: 0;">
                <div class="d-flex justify-content-between">
                  <span class="text-xs font-weight-bold text-truncate me-2">{{ v.company_name }}</span>
                  <span class="text-xs font-weight-bolder text-nowrap">{{ shortIdr(v.spend) }}</span>
                </div>
                <div class="cat-bar-bg mt-1"><div class="cat-bar bg-gradient-info" :style="{ width: (v.spend / (stats.top[0].spend || 1) * 100) + '%' }"></div></div>
                <span class="text-xxs text-secondary">{{ v.po_count }} PO</span>
              </div>
            </div>
            <p class="text-xxs text-warning mb-0" v-if="stats.missingRates.length">
              <i class="fa fa-exclamation-triangle"></i> Kurs {{ stats.missingRates.join(', ') }} belum diisi (Budget Control → Kurs), PO mata uang tersebut belum ikut dihitung.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- ======= TABEL ======= -->
    <div class="card">
      <div class="card-header pb-2">
        <div class="row align-items-center">
          <div class="col-lg-5 mb-2">
            <div class="input-group">
              <span class="input-group-text"><i class="fa fa-search"></i></span>
              <input type="text" class="form-control" placeholder="Cari vendor, produk/jasa (mis. IT, cutting disc), atau PIC..." v-model="search.q" @input="onSearch()">
            </div>
          </div>
          <div class="col-lg-7 mb-2 d-flex flex-wrap justify-content-lg-end align-items-center">
            <div class="btn-group me-2 mb-1">
              <button v-for="s in statusTabs" :key="s.value" class="btn btn-sm mb-0" :class="search.status == s.value ? 'btn-dark' : 'btn-outline-dark'" @click="setStatus(s.value)">{{ s.label }}</button>
            </div>
            <button class="btn btn-sm mb-1 me-2" :class="search.npwp == 'none' ? 'btn-warning' : 'btn-outline-warning'" @click="search.npwp = search.npwp == 'none' ? '' : 'none'; reload()">Tanpa NPWP</button>
            <select class="form-select form-select-sm me-2 mb-1" style="width: auto;" v-model="search.rating" @change="reload()">
              <option value="">Semua Rating</option>
              <option value="recommended">Direkomendasikan</option>
              <option value="fair">Cukup</option>
              <option value="evaluate">Perlu Evaluasi</option>
              <option value="blacklist">Sarankan Blacklist</option>
              <option value="none">Belum Dinilai</option>
            </select>
            <argon-button class="mb-1" variant="gradient" color="secondary" size="sm" @click="filter()"><i class="fa fa-filter fa-sm"></i> Filter Lanjutan</argon-button>
          </div>
        </div>
        <div v-if="activeFilters.length" class="mt-1">
          <span class="badge bg-light text-dark me-1 filter-chip" v-for="f in activeFilters" :key="f.key" @click="clearFilter(f.key)">{{ f.label }} <i class="fa fa-times ms-1"></i></span>
          <a href="javascript:;" class="text-xs ms-1" @click="resetFilter()">Reset semua</a>
        </div>
        <span class="text-xs text-secondary">Menampilkan {{ table.data.length }} dari {{ pagination.total }} vendor &middot; <i class="fas fa-sort-amount-down"></i> diurutkan dari rating terbaik, lalu jumlah PO terbanyak</span>
      </div>

      <div class="card-body px-0 pt-0 pb-2">
        <div class="table-responsive p-0 scroll">
          <table class="table table-hover align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-uppercase text-white text-xxs font-weight-bolder ps-3">Vendor</th>
                <th class="text-uppercase text-white text-xxs font-weight-bolder">Produk / Jasa</th>
                <th class="text-uppercase text-white text-xxs font-weight-bolder">Kategori</th>
                <th class="text-uppercase text-white text-xxs font-weight-bolder">PIC & Kontak</th>
                <th class="text-uppercase text-white text-xxs font-weight-bolder">NPWP</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">PO</th>
                <th class="text-end text-uppercase text-white text-xxs font-weight-bolder">Nilai PO (IDR)</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">PO Terakhir</th>
                <th class="text-end text-uppercase text-white text-xxs font-weight-bolder">Sisa Hutang</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">Rating</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">Status</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!table.data.length">
                <td colspan="12" class="text-center text-sm text-secondary py-5"><i class="fa fa-inbox fa-2x d-block mb-2"></i>Tidak ada vendor yang cocok dengan filter</td>
              </tr>
              <tr v-for="row in table.data" :key="row.id">
                <td class="ps-3" style="max-width: 320px;">
                  <div class="d-flex align-items-center">
                    <div class="avatar-initial me-2" :style="{ background: categoryColor(row.vendor_category) }">{{ initials(row.company_name) }}</div>
                    <div style="min-width: 0;">
                      <a href="javascript:;" class="text-sm font-weight-bold text-dark d-block text-truncate" @click="openDetail(row.vendor_code)">{{ row.company_name }}</a>
                      <span class="text-xxs text-secondary">{{ row.vendor_code }}</span>
                      <span class="text-xxs text-secondary d-block text-truncate" :title="row.address">{{ clean(row.address) }}</span>
                    </div>
                  </div>
                </td>
                <td style="max-width: 230px; white-space: normal;">
                  <span class="product-chip" v-for="p in productList(row.products).slice(0, 4)" :key="p" :class="{ 'chip-hit': isHit(p) }">{{ p }}</span>
                  <span class="text-xxs text-secondary" v-if="productList(row.products).length > 4" :title="row.products">+{{ productList(row.products).length - 4 }} lainnya</span>
                  <span class="text-xxs text-secondary" v-if="!productList(row.products).length && !(row.matched_items || []).length">-</span>
                  <div v-if="(row.matched_items || []).length" class="mt-1">
                    <span class="history-chip" v-for="m in row.matched_items" :key="m.part_name" title="Dari riwayat RFQ/PO vendor ini"><i class="fas fa-history"></i> {{ m.part_name.trim() }} &times;{{ m.total }}</span>
                  </div>
                </td>
                <td>
                  <span class="badge badge-sm cat-badge" :style="{ background: categoryColor(row.vendor_category) }">{{ row.vendor_category || '-' }}</span>
                  <span class="text-xxs text-secondary d-block mt-1 text-truncate" style="max-width: 150px;" :title="row.item_group">{{ clean(row.item_group) }}</span>
                </td>
                <td>
                  <span class="text-xs font-weight-bold d-block">{{ clean(row.contact_person) }}</span>
                  <span class="text-xxs text-secondary d-block" v-if="clean(row.email) != '-'"><i class="far fa-envelope"></i> {{ row.email }}</span>
                  <span class="text-xxs text-secondary d-block" v-if="contactPhone(row) != '-'"><i class="fa fa-phone"></i> {{ contactPhone(row) }}</span>
                </td>
                <td>
                  <span v-if="hasNpwp(row.npwp)" class="text-xs text-success font-weight-bold"><i class="fa fa-check-circle"></i> {{ row.npwp }}</span>
                  <span v-else class="badge badge-sm bg-gradient-warning" title="PPh 23 dipotong 2x tarif untuk vendor tanpa NPWP">Tanpa NPWP</span>
                </td>
                <td class="text-center"><span class="text-sm font-weight-bolder">{{ statOf(row).po_count || '-' }}</span></td>
                <td class="text-end"><span class="text-xs font-weight-bold">{{ statOf(row).spend ? idr(statOf(row).spend) : '-' }}</span></td>
                <td class="text-center">
                  <span class="text-xs" v-if="statOf(row).last_po_date">{{ moment(statOf(row).last_po_date).locale('id').format('ll') }}<br><span class="text-xxs text-secondary">{{ moment(statOf(row).last_po_date).locale('id').fromNow() }}</span></span>
                  <span class="text-xs text-secondary" v-else>Belum ada PO</span>
                </td>
                <td class="text-end">
                  <span class="text-xs font-weight-bold" :class="statOf(row).outstanding > 0 ? 'text-danger' : 'text-secondary'">{{ statOf(row).outstanding > 0 ? idr(statOf(row).outstanding) : '-' }}</span>
                </td>
                <td class="text-center">
                  <template v-if="ratingOf(row)">
                    <star-row :value="ratingOf(row).average" small />
                    <span class="text-xxs d-block"><b>{{ ratingOf(row).average.toFixed(1) }}</b> &middot; {{ ratingOf(row).count ? ratingOf(row).count + ' penilaian' : 'skor sistem' }}</span>
                    <span class="reco-badge reco-sm" :class="'reco-' + ratingOf(row).level">{{ ratingOf(row).level == 'blacklist' ? 'Sarankan Blacklist' : ratingOf(row).label }}</span>
                  </template>
                  <span class="text-xxs text-secondary" v-else>Belum dinilai</span>
                </td>
                <td class="text-center">
                  <span class="badge badge-sm bg-gradient-success" v-if="row.status == 'Active'">Active</span>
                  <span class="badge badge-sm bg-gradient-dark" v-else-if="row.status == 'Blacklist'">Blacklist</span>
                  <span class="badge badge-sm bg-light text-secondary" v-else>Belum diisi</span>
                </td>
                <td class="text-center text-nowrap">
                  <i class="fa fa-eye action-icon text-info" title="Detail & riwayat pembelian" @click="openDetail(row.vendor_code)"></i>
                  <i class="fas fa-edit action-icon text-primary" title="Edit data vendor ini" @click="edit(row.id)"></i>
                  <i class="fa fa-trash action-icon text-danger" title="Hapus vendor ini" @click="remove(row.id)"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="card-footer pt-0">
        <div class="float-right">
          <base-pagination :page-count="pagination.page_count" :value="pagination.page" @input="changePage" :total="pagination.page_count" class="justify-content-end"></base-pagination>
        </div>
      </div>
    </div>
  </div>

  <!-- =======  MODAL ADD / EDIT ======= -->
  <div class="container">
    <vue-final-modal v-model="form.show" classes="modal-container" content-class="modal-content modal-form" :z-index="10000">
      <div class="d-flex justify-content-between align-items-center">
        <span class="modal__title">{{ form.title == 'Add Data' ? 'Tambah Vendor' : 'Edit Vendor' }}</span>
        <i style="cursor: pointer;" class="fa fa-times" @click="form.show = false"></i>
      </div><hr>
      <div class="modal__content">
        <p class="section-title">Identitas</p>
        <div class="row">
          <div class="col-md-8">
            <label class="form-control-label">Company Name <span class="text-danger">*</span></label>
            <input type="text" class="form-control" placeholder="Company Name" v-model="vendorData.company_name">
          </div>
          <div class="col-md-4">
            <label class="form-control-label">Vendor Status <span class="text-danger">*</span></label>
            <select class="form-select" v-model="vendorData.status">
              <option value="Active">Active</option>
              <option value="Blacklist">Blacklist</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-control-label mt-2">NPWP <span class="text-danger">*</span></label>
            <input type="text" class="form-control" placeholder="Isi '-' jika tidak ada" v-model="vendorData.npwp">
            <span class="text-xxs text-warning" v-if="vendorData.npwp !== undefined && !hasNpwp(vendorData.npwp)">Tanpa NPWP: PPh 23 dipotong 2x tarif</span>
          </div>
          <div class="col-md-6">
            <label class="form-control-label mt-2">Vendor Category <span class="text-danger">*</span></label>
            <select class="form-select" v-model="vendorData.vendor_category">
              <option v-for="c in categoryOptions" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>
          <div class="col-12">
            <label class="form-control-label mt-2">Item Group <span class="text-danger">*</span></label>
            <input type="text" class="form-control" placeholder="Contoh: Plate, Pipe, Consumable" v-model="vendorData.item_group">
          </div>
          <div class="col-12">
            <label class="form-control-label mt-2">Produk / Jasa yang Disediakan <span class="text-xs text-secondary">(pisahkan dengan koma)</span></label>
            <textarea class="form-control" rows="2" placeholder="Contoh: IT, Laptop, CCTV, Cutting Disc, Grinding Wheel" v-model="vendorData.products"></textarea>
            <div class="mt-1" v-if="productList(vendorData.products).length">
              <span class="product-chip" v-for="p in productList(vendorData.products)" :key="p">{{ p }}</span>
            </div>
            <span class="text-xxs text-secondary">Dipakai untuk pencarian cepat, misalnya cari "IT" atau "cutting disc".</span>
          </div>
        </div>
        <p class="section-title mt-3">Kontak</p>
        <div class="row">
          <div class="col-md-6">
            <label class="form-control-label">Contact Person <span class="text-danger">*</span></label>
            <input type="text" class="form-control" placeholder="Contact Person" v-model="vendorData.contact_person">
          </div>
          <div class="col-md-6">
            <label class="form-control-label">Email <span class="text-danger">*</span></label>
            <input type="email" class="form-control" placeholder="Email" v-model="vendorData.email">
          </div>
          <div class="col-md-4">
            <label class="form-control-label mt-2">No HP</label>
            <input type="text" class="form-control" placeholder="No HP" v-model="vendorData.no_hp">
          </div>
          <div class="col-md-4">
            <label class="form-control-label mt-2">Phone</label>
            <input type="text" class="form-control" placeholder="Phone" v-model="vendorData.phone">
          </div>
          <div class="col-md-4">
            <label class="form-control-label mt-2">Fax</label>
            <input type="text" class="form-control" placeholder="Fax" v-model="vendorData.fax">
          </div>
          <div class="col-12">
            <label class="form-control-label mt-2">Address <span class="text-danger">*</span></label>
            <textarea class="form-control" rows="2" placeholder="Address" v-model="vendorData.address"></textarea>
          </div>
          <div class="col-12">
            <label class="form-control-label mt-2">Vendor Review</label>
            <textarea class="form-control" rows="2" v-model="vendorData.note" placeholder="Catatan performa / review vendor"></textarea>
          </div>
        </div>
        <p class="section-title mt-3">Rekening Bank <span class="text-xxs text-secondary text-lowercase">(untuk pembayaran di Accounting)</span></p>
        <div class="row">
          <div class="col-md-4">
            <label class="form-control-label">Nama Bank</label>
            <input type="text" class="form-control" placeholder="Contoh: BCA, Mandiri, BRI" v-model="vendorData.bank_name">
          </div>
          <div class="col-md-4">
            <label class="form-control-label">Cabang</label>
            <input type="text" class="form-control" placeholder="Cabang (opsional)" v-model="vendorData.bank_branch">
          </div>
          <div class="col-md-4">
            <label class="form-control-label">No. Rekening</label>
            <input type="text" class="form-control" placeholder="No. Rekening" v-model="vendorData.bank_account_no">
          </div>
          <div class="col-12">
            <label class="form-control-label mt-2">Atas Nama</label>
            <input type="text" class="form-control" placeholder="Nama pemilik rekening" v-model="vendorData.bank_account_name">
            <span class="text-xxs text-warning" v-if="bankNameMismatch">Nama pemilik rekening berbeda dengan nama vendor, pastikan sudah benar.</span>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-end mt-3">
        <argon-button class="me-2" variant="gradient" color="secondary" size="sm" @click="form.show = false">Close</argon-button>
        <argon-button variant="gradient" color="success" size="sm" @click="save()">Save</argon-button>
      </div>
    </vue-final-modal>
  </div>

  <!-- =======  MODAL FILTER ======= -->
  <div class="container">
    <vue-final-modal v-model="formFilter.show" classes="modal-container" content-class="modal-content" :z-index="10000">
      <div class="d-flex justify-content-between align-items-center">
        <span class="modal__title">Filter Lanjutan</span>
        <i style="cursor: pointer;" class="fa fa-times" @click="formFilter.show = false"></i>
      </div><hr>
      <div class="modal__content container">
        <label class="form-control-label mt-2">Vendor Code</label>
        <input type="text" class="form-control" placeholder="Vendor Code" v-model="search.vendor_code" v-on:keyup.enter="reload()">
        <label class="form-control-label mt-2">Address</label>
        <input type="text" class="form-control" placeholder="Address" v-model="search.address" v-on:keyup.enter="reload()">
        <label class="form-control-label mt-2">Email</label>
        <input type="email" class="form-control" placeholder="Email" v-model="search.email" v-on:keyup.enter="reload()">
        <label class="form-control-label mt-2">Vendor Category</label>
        <select class="form-select" v-model="search.vendor_category">
          <option value="">Semua</option>
          <option v-for="c in categoryOptions" :key="c" :value="c">{{ c }}</option>
        </select>
        <label class="form-control-label mt-2">Item Group</label>
        <input type="text" class="form-control" placeholder="Item Group" v-model="search.item_group" v-on:keyup.enter="reload()">
      </div>
      <div class="d-flex justify-content-end mt-3">
        <argon-button class="me-2" variant="gradient" color="secondary" size="sm" @click="resetFilter()">Clear</argon-button>
        <argon-button variant="gradient" color="success" size="sm" @click="reload()">Filter</argon-button>
      </div>
    </vue-final-modal>
  </div>

  <!-- ======= DETAIL VENDOR & RIWAYAT PEMBELIAN ======= -->
  <div class="container">
    <vue-final-modal v-model="detail.show" classes="modal-container" content-class="modal-content-width" :z-index="10000" :focus-retain="false">
      <template v-if="detail.vendor">
        <!-- header -->
        <div class="d-flex justify-content-between align-items-start">
          <div class="d-flex align-items-center">
            <div class="avatar-initial avatar-lg me-3" :style="{ background: categoryColor(detail.vendor.vendor_category) }">{{ initials(detail.vendor.company_name) }}</div>
            <div>
              <span class="modal__title">{{ detail.vendor.company_name }}</span>
              <span class="badge badge-sm bg-gradient-success ms-2" v-if="detail.vendor.status == 'Active'">Active</span>
              <span class="badge badge-sm bg-gradient-dark ms-2" v-else-if="detail.vendor.status == 'Blacklist'">Blacklist</span>
              <div class="mt-1">
                <span class="info-chip">{{ detail.vendor.vendor_code }}</span>
                <span class="info-chip" :style="{ color: categoryColor(detail.vendor.vendor_category) }"><i class="fa fa-tag"></i> {{ detail.vendor.vendor_category }}</span>
                <span class="product-chip" v-for="p in productList(detail.vendor.products)" :key="'p' + p">{{ p }}</span>
                <span class="info-chip text-success" v-if="hasNpwp(detail.vendor.npwp)"><i class="fa fa-check-circle"></i> NPWP {{ detail.vendor.npwp }}</span>
                <span class="info-chip text-warning" v-else title="PPh 23 dipotong 2x tarif"><i class="fa fa-exclamation-circle"></i> Tanpa NPWP</span>
              </div>
            </div>
          </div>
          <i style="cursor: pointer;" class="fa fa-times fa-lg" @click="detail.show = false"></i>
        </div>

        <!-- angka utama -->
        <div class="stat-strip mt-3">
          <div class="stat-item" v-for="m in detailMetrics" :key="m.label">
            <i :class="m.icon" class="stat-icon"></i>
            <div>
              <span class="stat-label">{{ m.label }}</span>
              <span class="stat-value" :class="m.cls">{{ m.value }}</span>
              <span class="stat-note" v-if="m.note">{{ m.note }}</span>
            </div>
          </div>
        </div>

        <div class="row mt-3 modal-scroll-body">
          <!-- penilaian vendor -->
          <div class="col-12 mb-3">
            <div class="rating-panel" :class="'rating-' + rating.recommendation.level">
              <div class="rating-score">
                <span class="rating-big">{{ (rating.count || rating.auto) ? rating.average.toFixed(1) : '-' }}</span>
                <div>
                  <star-row :value="rating.average" />
                  <span class="text-xxs text-secondary d-block">Nilai akhir</span>
                  <span class="text-xxs text-secondary d-block">User: <b>{{ rating.manualAverage ? Number(rating.manualAverage).toFixed(1) : '-' }}</b> ({{ rating.count }}x) &middot; Sistem: <b>{{ autoOverall ? autoOverall.toFixed(1) : '-' }}</b></span>
                </div>
              </div>
              <div class="rating-reco">
                <span class="stat-label">Rekomendasi Sistem</span>
                <span class="reco-badge" :class="'reco-' + rating.recommendation.level">{{ rating.recommendation.label }}</span>
                <span class="text-xxs text-secondary d-block mt-1" v-if="rating.recommendation.level == 'blacklist' && detail.vendor.status != 'Blacklist'">
                  Nilai rata-rata di bawah 2,0. Pertimbangkan untuk mem-blacklist vendor ini.
                </span>
                <div class="mt-2">
                  <button class="btn btn-sm btn-primary mb-0 me-2" @click="openRatingForm()"><i class="fas fa-star"></i> Beri Penilaian</button>
                  <button class="btn btn-sm mb-0" :class="rating.recommendation.level == 'blacklist' ? 'btn-danger' : 'btn-outline-dark'" v-if="detail.vendor.status != 'Blacklist'" @click="openStatusForm('Blacklist')"><i class="fas fa-ban"></i> Blacklist</button>
                  <button class="btn btn-sm btn-outline-success mb-0" v-else @click="openStatusForm('Active')"><i class="fas fa-undo"></i> Aktifkan Kembali</button>
                </div>
              </div>
              <div class="rating-aspects">
                <div class="aspect-row" v-for="a in rating.perAspect" :key="a.id">
                  <span class="aspect-name">{{ a.name }}</span>
                  <div class="cat-bar-bg"><div class="cat-bar" :style="{ width: ((a.average || 0) / 5 * 100) + '%', background: scoreColor(a.average) }"></div></div>
                  <span class="aspect-val">{{ a.average ? a.average.toFixed(1) : '-' }}</span>
                </div>
              </div>
            </div>
            <div class="auto-score mt-2">
              <span class="stat-label mb-1"><i class="fas fa-robot"></i> Skor Otomatis dari Data Sistem</span>
              <div class="auto-grid">
                <div class="auto-item" v-for="a in autoItems" :key="a.label">
                  <div class="d-flex justify-content-between">
                    <span class="text-xs font-weight-bold">{{ a.label }}</span>
                    <span class="text-xs font-weight-bolder" :style="{ color: scoreColor(a.score) }">{{ a.score ? a.score.toFixed(1) : '-' }}</span>
                  </div>
                  <div class="cat-bar-bg mt-1"><div class="cat-bar" :style="{ width: ((a.score || 0) / 5 * 100) + '%', background: scoreColor(a.score) }"></div></div>
                  <span class="text-xxs text-secondary">{{ a.note }}</span>
                </div>
              </div>
            </div>
            <div v-if="rating.ratings.length" class="mt-2">
              <a href="javascript:;" class="text-xs font-weight-bold" @click="rating.showList = !rating.showList">
                <i class="fas" :class="rating.showList ? 'fa-chevron-up' : 'fa-chevron-down'"></i> Riwayat Penilaian ({{ rating.ratings.length }})
              </a>
              <div v-if="rating.showList" class="mt-2">
                <div class="rating-item" v-for="r in rating.ratings" :key="r.id">
                  <div class="d-flex justify-content-between">
                    <div>
                      <span class="text-sm font-weight-bold me-2">{{ Number(r.overall).toFixed(1) }}</span>
                      <star-row :value="Number(r.overall)" small />
                      <span class="info-chip ms-2" v-if="r.po_no">{{ r.po_no }}</span>
                    </div>
                    <div class="text-nowrap">
                      <span class="text-xxs text-secondary me-2">{{ r.rated_by }} &middot; {{ moment(r.updated_at).locale('id').format('ll') }}</span>
                      <i class="fas fa-edit action-icon text-primary" title="Edit penilaian" @click="openRatingForm(r)"></i>
                      <i class="fa fa-trash action-icon text-danger" title="Hapus penilaian" @click="removeRating(r.id)"></i>
                    </div>
                  </div>
                  <div class="mt-1">
                    <span class="text-xxs text-secondary me-3" v-for="a in rating.aspects" :key="a.id">{{ a.name }}: <b :style="{ color: scoreColor(r.scores[a.id]) }">{{ r.scores[a.id] || '-' }}</b></span>
                  </div>
                  <p class="text-xs mb-0 mt-1" v-if="r.note">"{{ r.note }}"</p>
                </div>
              </div>
            </div>
          </div>

          <!-- kiri: kontak -->
          <div class="col-lg-4">
            <p class="section-title">Kontak</p>
            <div class="contact-list">
              <div v-for="c in contactRows" :key="c.label" class="contact-row">
                <i :class="c.icon"></i>
                <div>
                  <span class="contact-label">{{ c.label }}</span>
                  <span class="contact-value">{{ c.value }}</span>
                </div>
              </div>
              <span class="text-xs text-secondary" v-if="!contactRows.length">Data kontak belum diisi</span>
            </div>

            <p class="section-title mt-3">Paling Sering Dibeli</p>
            <div v-if="topItems.length">
              <div class="top-item" v-for="it in topItems" :key="it.name">
                <span class="text-truncate">{{ it.name }}</span>
                <span class="top-item-count">{{ it.count }}x</span>
              </div>
            </div>
            <span class="text-xs text-secondary" v-else>Belum ada pembelian</span>
          </div>

          <!-- kanan: riwayat per PO -->
          <div class="col-lg-8">
            <p class="section-title">Riwayat Pembelian &middot; {{ poGroups.length }} PO, {{ historyRows.length }} item</p>
            <div class="text-center text-xs text-secondary py-5 empty-box" v-if="!poGroups.length">
              <i class="fa fa-inbox fa-2x d-block mb-2"></i>Belum ada riwayat pembelian dari vendor ini
            </div>
            <div class="po-card" v-for="po in poGroups" :key="po.po_no">
              <div class="po-head">
                <div>
                  <span class="po-no">{{ po.po_no }}</span>
                  <span class="po-date">{{ po.date ? moment(po.date).locale('id').format('LL') : '-' }}</span>
                </div>
                <span class="po-total">{{ money(po.total, po.currency) }}</span>
              </div>
              <div class="po-meta">
                <span v-if="po.terms != '-'"><i class="fa fa-credit-card"></i> {{ po.terms }}</span>
                <span v-if="po.delivery != '-'"><i class="fa fa-truck"></i> {{ po.delivery }}</span>
                <span v-if="po.pic"><i class="fa fa-user"></i> {{ po.pic }}</span>
              </div>
              <div class="po-item" v-for="(item, k) in po.items" :key="k">
                <div class="po-item-name">
                  <span class="text-sm font-weight-bold">{{ item.part_name }}</span>
                  <span class="text-xxs text-secondary d-block">{{ [clean(item.specification), clean(item.size)].filter(v => v != '-').join(' · ') }}</span>
                </div>
                <div class="po-item-calc">
                  <span class="text-xs text-secondary">{{ Number(item.qty) }} {{ item.unit }} &times; {{ money(item.unit_price, item.currency) }}</span>
                  <span class="text-xxs text-danger d-block" v-if="Number(item.discount)">Disc {{ money(item.discount, item.currency) }}</span>
                </div>
                <span class="po-item-amount">{{ money(item.amount, item.currency) }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </vue-final-modal>
  </div>

  <!-- ======= FORM PENILAIAN ======= -->
  <div class="container">
    <vue-final-modal v-model="ratingForm.show" classes="modal-container" content-class="modal-content modal-rating" :z-index="10001">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <span class="modal__title d-block">{{ ratingForm.id ? 'Edit Penilaian' : 'Beri Penilaian' }}</span>
          <span class="text-xs text-secondary" v-if="detail.vendor">{{ detail.vendor.company_name }}</span>
        </div>
        <i style="cursor: pointer;" class="fa fa-times" @click="ratingForm.show = false"></i>
      </div><hr>
      <label class="form-control-label">PO yang dinilai <span class="text-xs text-secondary">(opsional, 1 penilaian per PO)</span></label>
      <select class="form-select mb-3" v-model="ratingForm.po_no">
        <option value="">- Penilaian umum (tanpa PO) -</option>
        <option v-for="po in poGroups" :key="po.po_no" :value="po.po_no">{{ po.po_no }} &middot; {{ po.date ? moment(po.date).locale('id').format('ll') : '' }}{{ ratedPo(po.po_no) ? ' (sudah dinilai)' : '' }}</option>
      </select>
      <div class="aspect-input" v-for="a in rating.aspects" :key="a.id">
        <div>
          <span class="text-sm font-weight-bold d-block">{{ a.name }}</span>
          <span class="text-xxs text-secondary">{{ a.description }}</span>
        </div>
        <div class="star-input text-nowrap">
          <i v-for="n in 5" :key="n" class="fa-star" :class="n <= (ratingForm.hover[a.id] || ratingForm.scores[a.id] || 0) ? 'fas' : 'far'"
             @mouseenter="ratingForm.hover[a.id] = n" @mouseleave="ratingForm.hover[a.id] = 0" @click="ratingForm.scores[a.id] = n"></i>
          <span class="star-text">{{ starLabels[(ratingForm.hover[a.id] || ratingForm.scores[a.id] || 0)] }}</span>
        </div>
      </div>
      <label class="form-control-label mt-3">Catatan</label>
      <textarea class="form-control" rows="2" v-model="ratingForm.note" placeholder="Contoh: barang datang telat 3 hari, harga lebih mahal dari vendor lain"></textarea>
      <div class="d-flex justify-content-between align-items-center mt-3">
        <span class="text-sm">Nilai keseluruhan: <b>{{ formOverall ? formOverall.toFixed(2) : '-' }}</b></span>
        <div>
          <argon-button class="me-2" variant="gradient" color="secondary" size="sm" @click="ratingForm.show = false">Close</argon-button>
          <argon-button variant="gradient" color="success" size="sm" @click="saveRating()">Simpan</argon-button>
        </div>
      </div>
    </vue-final-modal>
  </div>

  <!-- ======= UBAH STATUS (BLACKLIST / AKTIFKAN) ======= -->
  <div class="container">
    <vue-final-modal v-model="statusForm.show" classes="modal-container" content-class="modal-content" :z-index="10001">
      <span class="modal__title">{{ statusForm.status == 'Blacklist' ? 'Blacklist Vendor' : 'Aktifkan Kembali Vendor' }}</span><hr>
      <p class="text-sm mb-2" v-if="detail.vendor">
        <b>{{ detail.vendor.company_name }}</b> akan diubah menjadi <b>{{ statusForm.status }}</b>.
        <span v-if="rating.count"> Rating saat ini {{ rating.average.toFixed(1) }} ({{ rating.recommendation.label }}).</span>
      </p>
      <label class="form-control-label">Alasan <span class="text-danger" v-if="statusForm.status == 'Blacklist'">*</span></label>
      <textarea class="form-control" rows="3" v-model="statusForm.reason" placeholder="Alasan akan dicatat di Vendor Review"></textarea>
      <div class="d-flex justify-content-end mt-3">
        <argon-button class="me-2" variant="gradient" color="secondary" size="sm" @click="statusForm.show = false">Batal</argon-button>
        <argon-button variant="gradient" :color="statusForm.status == 'Blacklist' ? 'danger' : 'success'" size="sm" @click="saveStatus()">Ya, {{ statusForm.status == 'Blacklist' ? 'Blacklist' : 'Aktifkan' }}</argon-button>
      </div>
    </vue-final-modal>
  </div>
</template>

<script>
import ArgonButton from "@/components/ArgonButton.vue";
import { VueFinalModal } from 'vue-final-modal'
import Api from '@/helpers/api';
import config from '@/configs/config';
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';
import BasePagination from "@/components/BasePagination";
var moment = require('moment');
import { h } from 'vue';
import vendorList from '@/services/procurement/vendorList.service';

// tampilan bintang 1-5 (mendukung setengah bintang)
const StarRow = {
  props: { value: { type: Number, default: 0 }, small: Boolean },
  render() {
    const stars = [1, 2, 3, 4, 5].map(i => {
      const cls = this.value >= i - 0.25 ? 'fas fa-star' : (this.value >= i - 0.75 ? 'fas fa-star-half-alt' : 'far fa-star');
      return h('i', { class: cls });
    });
    return h('span', { class: 'star-row' + (this.small ? ' star-sm' : '') }, stars);
  },
};

const EMPTY_RATING = { aspects: [], average: 0, count: 0, recommendation: { level: 'none', label: 'Belum Dinilai' }, perAspect: [], ratings: [], showList: false };

const CATEGORY_COLORS = {
  'Supplier': '#5e72e4', 'Services': '#11cdef', 'Manufacture': '#fb6340', 'Agent': '#8965e0', 'Equipment': '#2dce89',
  'Transport': '#f5365c', 'Consumable': '#ffd600', 'Trading Company': '#172b4d', 'Tools': '#ff8d72', 'Mill Maker': '#6c757d',
};

export default {
  name: "VendorList",
  components: { ArgonButton, VueFinalModal, BasePagination, StarRow },
  data() {
    return {
      moment: moment,
      apiUrl: config.apiUrl,
      pagination: { page_count: '', page: 1, total: 0 },
      table: { data: [] },
      stats: { summary: {}, categories: {}, top: [], stats: {}, missingRates: [] },
      form: { title: "Add Data", show: false },
      formFilter: { show: false },
      detail: { show: false, vendor: null, history: [] },
      rating: { ...EMPTY_RATING },
      ratingForm: { show: false, id: null, po_no: '', scores: {}, hover: {}, note: '' },
      statusForm: { show: false, status: 'Blacklist', reason: '' },
      starLabels: ['Belum dinilai', 'Sangat Buruk', 'Buruk', 'Cukup', 'Baik', 'Sangat Baik'],
      vendorData: {},
      categoryOptions: ['Agent', 'Consumable', 'Equipment', 'Manufacture', 'Mill Maker', 'Services', 'Supplier', 'Tools', 'Trading Company', 'Transport'],
      statusTabs: [{ label: 'Semua', value: '' }, { label: 'Active', value: 'Active' }, { label: 'Blacklist', value: 'Blacklist' }, { label: 'Belum diisi', value: 'none' }],
      search: { q: '', vendor_code: '', address: '', email: '', vendor_category: '', item_group: '', status: '', npwp: '', rating: '' },
      searchTimer: null,
      requestSeq: 0,
    };
  },
  computed: {
    kpis() {
      const s = this.stats.summary;
      return [
        { label: 'Total Vendor', value: s.total || 0, icon: 'fa fa-building', color: 'primary', note: (s.with_po || 0) + ' vendor sudah pernah PO', action: () => this.resetFilter() },
        { label: 'Active', value: s.active || 0, icon: 'fa fa-check', color: 'success', note: 'Klik untuk filter', action: () => this.setStatus('Active') },
        { label: 'Blacklist', value: s.blacklist || 0, icon: 'fa fa-ban', color: 'dark', note: 'Klik untuk filter', action: () => this.setStatus('Blacklist') },
        { label: 'Status Belum Diisi', value: s.no_status || 0, icon: 'fa fa-question', color: 'secondary', note: 'Perlu dilengkapi', action: () => this.setStatus('none') },
        { label: 'Tanpa NPWP', value: s.without_npwp || 0, icon: 'fas fa-file-invoice', color: 'warning', note: 'PPh 23 dipotong 2x tarif', action: () => { this.search.npwp = 'none'; this.reload(); } },
        { label: 'Sarankan Blacklist', value: s.suggest_blacklist || 0, icon: 'fas fa-star-half-alt', color: 'danger', note: (s.rated || 0) + ' vendor sudah dinilai', action: () => { this.search.rating = 'blacklist'; this.reload(); } },
        { label: 'Total Nilai PO', value: this.shortIdr(s.total_spend || 0), icon: 'fa fa-shopping-cart', color: 'info', note: 'Sisa hutang ' + this.shortIdr(s.total_outstanding || 0) },
      ];
    },
    maxCategory() {
      return Math.max(1, ...Object.values(this.stats.categories));
    },
    activeFilters() {
      const labels = { vendor_code: 'Kode', address: 'Alamat', email: 'Email', vendor_category: 'Kategori', item_group: 'Item Group' };
      const list = Object.keys(labels).filter(k => this.search[k]).map(k => ({ key: k, label: labels[k] + ': ' + this.search[k] }));
      if (this.search.npwp == 'none') list.push({ key: 'npwp', label: 'Tanpa NPWP' });
      const ratingLabels = { recommended: 'Direkomendasikan', fair: 'Cukup', evaluate: 'Perlu Evaluasi', blacklist: 'Sarankan Blacklist', none: 'Belum Dinilai' };
      if (this.search.rating) list.push({ key: 'rating', label: 'Rating: ' + ratingLabels[this.search.rating] });
      return list;
    },
    historyRows() {
      return this.detail.history.filter(r => r.po_no);
    },
    detailStat() {
      return this.detail.vendor ? (this.stats.stats[this.detail.vendor.vendor_code] || {}) : {};
    },
    detailMetrics() {
      const st = this.detailStat;
      const rfqCount = new Set(this.detail.history.map(r => r.rfq_code)).size;
      return [
        { label: 'Jumlah PO', icon: 'fas fa-file-invoice', value: (st.po_count || 0) + ' PO', note: rfqCount ? rfqCount + ' penawaran (RFQ)' : '' },
        { label: 'Nilai PO', icon: 'fas fa-money-bill-wave', value: st.spend ? this.shortIdr(st.spend) : 'Rp 0', note: st.spend ? this.idr(st.spend) : '' },
        { label: 'Sisa Hutang', icon: 'fa fa-balance-scale', value: st.outstanding > 0 ? this.shortIdr(st.outstanding) : 'Rp 0', cls: st.outstanding > 0 ? 'text-danger' : 'text-success', note: st.paid ? 'Dibayar ' + this.shortIdr(st.paid) : '' },
        { label: 'PO Terakhir', icon: 'fa fa-calendar', value: st.last_po_date ? moment(st.last_po_date).locale('id').format('ll') : '-', note: st.last_po_date ? moment(st.last_po_date).locale('id').fromNow() : '' },
      ];
    },
    // hanya tampilkan kontak yang terisi
    contactRows() {
      const v = this.detail.vendor;
      if (!v) return [];
      return [
        { label: 'PIC', icon: 'fa fa-user', value: this.clean(v.contact_person) },
        { label: 'Email', icon: 'far fa-envelope', value: this.clean(v.email) },
        { label: 'No HP', icon: 'fas fa-mobile-alt', value: this.clean(v.no_hp) },
        { label: 'Phone', icon: 'fa fa-phone', value: this.clean(v.phone) },
        { label: 'Fax', icon: 'fa fa-fax', value: this.clean(v.fax) },
        { label: 'Item Group', icon: 'fa fa-cubes', value: this.clean(v.item_group) },
        { label: 'Alamat', icon: 'fas fa-map-marker-alt', value: this.clean(v.address) },
        { label: 'Review', icon: 'far fa-comment', value: this.clean(v.note) },
        { label: 'Rekening Bank', icon: 'fas fa-university', value: this.bankText(v) },
      ].filter(c => c.value != '-');
    },
    // riwayat dikelompokkan per PO, terbaru di atas
    poGroups() {
      const groups = {};
      this.historyRows.forEach(r => {
        if (!groups[r.po_no]) {
          groups[r.po_no] = { po_no: r.po_no, date: r.issue_date, currency: r.currency, terms: this.clean(r.payment_terms), delivery: this.clean(r.del_time), pic: r.created_by, total: 0, items: [] };
        }
        groups[r.po_no].items.push(r);
        groups[r.po_no].total += Number(r.amount || 0) - Number(r.discount || 0);
      });
      return Object.values(groups).sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
    },
    autoOverall() {
      return this.rating.auto && this.rating.auto.overall !== null ? Number(this.rating.auto.overall) : null;
    },
    // rincian skor sistem: kualitas (QC), pengiriman (tepat waktu), harga (vs penawaran termurah)
    autoItems() {
      const a = this.rating.auto || {};
      const num = v => (v === null || v === undefined) ? null : Number(v);
      return [
        { label: 'Kualitas (QC)', score: num(a.quality_score), note: a.quality_n ? a.quality_n + ' hasil QC' : 'Belum ada data QC Inspection' },
        { label: 'Ketepatan Pengiriman', score: num(a.delivery_score), note: a.delivery_n ? a.delivery_on_time + ' dari ' + a.delivery_n + ' PO tepat waktu' + (a.delivery_late_avg ? ', telat rata-rata ' + Number(a.delivery_late_avg) + ' hari' : '') + (a.delivery_n < 2 ? ' (data belum cukup, min. 2 PO)' : '') : 'Belum ada data penerimaan yang bisa dihitung' },
        { label: 'Harga', score: num(a.price_score), note: a.price_n ? 'Termurah di ' + a.price_cheapest + ' dari ' + a.price_n + ' item pembanding' + (a.price_n < 3 ? ' (data belum cukup, min. 3 item)' : '') : 'Belum ada penawaran pembanding' },
      ];
    },
    // atas nama rekening tidak mengandung nama vendor (tanpa PT/CV) -> beri peringatan
    bankNameMismatch() {
      const strip = v => String(v || '').toLowerCase().replace(/\b(pt|cv|ud|tbk)\b\.?/g, '').replace(/[^a-z0-9]/g, '');
      const owner = strip(this.vendorData.bank_account_name), company = strip(this.vendorData.company_name);
      return owner !== '' && company !== '' && !owner.includes(company) && !company.includes(owner);
    },
    formOverall() {
      let total = 0, weight = 0;
      this.rating.aspects.forEach(a => {
        const score = this.ratingForm.scores[a.id];
        if (score) { total += score * Number(a.weight); weight += Number(a.weight); }
      });
      return weight ? total / weight : 0;
    },
    topItems() {
      const counter = {};
      this.historyRows.forEach(r => { const n = (r.part_name || '').trim().replace(/\s+/g, ' ').toUpperCase(); if (n) counter[n] = (counter[n] || 0) + 1; });
      return Object.entries(counter).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([name, count]) => ({ name, count }));
    },
  },
  mounted() {
    this.get();
    this.getStats();
  },
  methods: {
    get() {
      let context = this;
      const s = this.search;
      // abaikan respons request lama (mis. muatan awal selesai setelah hasil pencarian)
      const seq = ++this.requestSeq;
      Api(context, vendorList.index({ q: s.q, vendor_code: s.vendor_code, address: s.address, email: s.email, vendor_category: s.vendor_category, item_group: s.item_group, status: s.status, npwp: s.npwp, rating: s.rating, page: context.pagination.page })).onSuccess(function(response) {
          if (seq !== context.requestSeq) return;
          const page = response.data.data.data;
          context.table.data            = page.data;
          context.pagination.page_count = page.last_page;
          context.pagination.total      = page.total;
      }).onError(function(error) {
          if (seq !== context.requestSeq) return;
          if (error.response && error.response.status == 404) {
              context.table.data       = [];
              context.pagination.total = 0;
          }
      }).onFinish(function() {
         context.formFilter.show = false;
      })
      .call()
    },
    getStats() {
      let context = this;
      Api(context, vendorList.stats()).onSuccess(function(response) {
          context.stats = response.data.data;
      }).call()
    },
    reload() {
      this.pagination.page = 1;
      this.get();
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.reload(), 350);
    },
    setStatus(status) {
      this.search.status = status;
      this.reload();
    },
    toggleCategory(name) {
      this.search.vendor_category = this.search.vendor_category == name ? '' : name;
      this.reload();
    },
    clearFilter(key) {
      this.search[key] = '';
      this.reload();
    },
    resetFilter() {
      this.search = { q: '', vendor_code: '', address: '', email: '', vendor_category: '', item_group: '', status: '', npwp: '', rating: '' };
      this.reload();
    },
    filter() {
      this.formFilter.show = true;
    },
    exportUrl(path) {
      const s = this.search;
      const params = { vendor_code: s.vendor_code, company_name: s.q, contact_person: '', address: s.address, email: s.email, vendor_category: s.vendor_category, item_group: s.item_group, status: s.status == 'none' ? '' : s.status };
      return this.apiUrl + path + '?' + Object.keys(params).map(k => k + '=' + encodeURIComponent(params[k] || '')).join('&');
    },
    // ===== format =====
    clean(value) {
      const v = (value === null || value === undefined) ? '' : String(value).trim();
      return (v === '' || v === '-' || v === '.' || v === '0' || v === 'undefined') ? '-' : v;
    },
    hasNpwp(npwp) {
      return /[1-9]/.test(String(npwp || ''));
    },
    contactPhone(row) {
      const hp = this.clean(row.no_hp), phone = this.clean(row.phone);
      return hp != '-' ? hp : phone;
    },
    initials(name) {
      const words = String(name || '?').replace(/^(PT|CV|UD)\.?\s+/i, '').split(/\s+/).filter(Boolean);
      return ((words[0] || '?')[0] + (words[1] ? words[1][0] : '')).toUpperCase();
    },
    categoryColor(name) {
      return CATEGORY_COLORS[name] || '#8392ab';
    },
    bankText(v) {
      if (!v || !v.bank_account_no) return '-';
      return [v.bank_name, v.bank_branch].filter(Boolean).join(' ') + ' ' + v.bank_account_no + (v.bank_account_name ? ' a.n. ' + v.bank_account_name : '');
    },
    productList(value) {
      const seen = new Set();
      return String(value || '').split(/[,;\n]+/).map(v => v.trim())
        .filter(v => v && !seen.has(v.toLowerCase()) && seen.add(v.toLowerCase()));
    },
    isHit(product) {
      const q = (this.search.q || '').trim().toLowerCase();
      return q !== '' && product.toLowerCase().includes(q);
    },
    ratingOf(row) {
      return (this.stats.ratings || {})[row.vendor_code];
    },
    scoreColor(score) {
      if (!score) return '#ced4da';
      return score >= 4 ? '#2dce89' : (score >= 3 ? '#11cdef' : (score >= 2 ? '#fb6340' : '#f5365c'));
    },
    ratedPo(poNo) {
      return this.rating.ratings.some(r => r.po_no == poNo && r.id != this.ratingForm.id);
    },
    getRating(vendorCode) {
      let context = this;
      Api(context, vendorList.rating(vendorCode)).onSuccess(function(response) {
          context.setRating(response.data.data);
      }).call()
    },
    setRating(data) {
      this.rating = { ...EMPTY_RATING, ...data, showList: this.rating.showList };
    },
    openRatingForm(existing) {
      this.ratingForm = {
        show: true,
        id: existing ? existing.id : null,
        po_no: existing ? (existing.po_no || '') : '',
        scores: existing ? { ...existing.scores } : {},
        hover: {},
        note: existing ? (existing.note || '') : '',
      };
    },
    saveRating() {
      let context = this;
      const f = this.ratingForm;
      const missing = this.rating.aspects.find(a => !f.scores[a.id]);
      if (missing) return alert('Beri bintang untuk aspek: ' + missing.name);
      if (!f.id && f.po_no && this.ratedPo(f.po_no) && !confirm('PO ' + f.po_no + ' sudah pernah dinilai. Ganti penilaian lama?')) return;

      let formData = new FormData();
      formData.append('vendor_code', this.detail.vendor.vendor_code);
      formData.append('po_no', f.po_no || '');
      formData.append('note', f.note || '');
      if (f.id) formData.append('id', f.id);
      Object.keys(f.scores).forEach(id => formData.append('scores[' + id + ']', f.scores[id]));

      Api(context, vendorList.saveRating(formData)).onSuccess(function(response) {
          context.setRating(response.data.data);
          context.ratingForm.show = false;
          context.getStats();
          context.notify(response.data.message, 'success');
      }).onError(function(error) {
          context.notify(error.response.data.message, 'error');
      }).call()
    },
    removeRating(id) {
      if (!confirm('Hapus penilaian ini?')) return;
      let context = this;
      Api(context, vendorList.deleteRating(id)).onSuccess(function(response) {
          context.setRating(response.data.data);
          context.getStats();
          context.notify(response.data.message, 'success');
      }).call()
    },
    openStatusForm(status) {
      this.statusForm = { show: true, status: status, reason: status == 'Blacklist' && this.rating.count ? 'Rating vendor ' + this.rating.average.toFixed(1) + ' (' + this.rating.recommendation.label + ')' : '' };
    },
    saveStatus() {
      let context = this;
      if (this.statusForm.status == 'Blacklist' && !this.statusForm.reason.trim()) return alert('Alasan blacklist wajib diisi');
      let formData = new FormData();
      formData.append('status', this.statusForm.status);
      formData.append('reason', this.statusForm.reason);
      Api(context, vendorList.setStatus(this.detail.vendor.vendor_code, formData)).onSuccess(function(response) {
          context.detail.vendor   = { ...context.detail.vendor, ...response.data.data };
          context.statusForm.show = false;
          context.get();
          context.getStats();
          context.notify(response.data.message, 'success');
      }).onError(function(error) {
          context.notify(error.response.data.message, 'error');
      }).call()
    },
    statOf(row) {
      return this.stats.stats[row.vendor_code] || {};
    },
    idr(value) {
      return 'Rp ' + Number(value || 0).toLocaleString('id-ID', { maximumFractionDigits: 0 });
    },
    shortIdr(value) {
      const v = Number(value || 0);
      if (v >= 1e9) return 'Rp ' + (v / 1e9).toLocaleString('id-ID', { maximumFractionDigits: 2 }) + ' M';
      if (v >= 1e6) return 'Rp ' + (v / 1e6).toLocaleString('id-ID', { maximumFractionDigits: 1 }) + ' jt';
      return this.idr(v);
    },
    money(value, currency) {
      if (value === null || value === undefined || value === '') return '-';
      return (currency && currency != 'IDR' ? currency + ' ' : 'Rp ') + Number(value).toLocaleString('id-ID', { maximumFractionDigits: 2 });
    },
    // ===== CRUD =====
    create() {
      this.form.show  = true;
      this.form.title = "Add Data";
      this.vendorData = { status: 'Active' };
    },
    edit(id) {
      let context = this;
      Api(context, vendorList.show(id)).onSuccess(function(response) {
          context.vendorData = response.data.data;
          context.form.show  = true;
          context.form.title = 'Edit Data';
      })
      .call()
    },
    save() {
      let api      = null;
      let context  = this;
      let formData = new FormData();
      const d      = context.vendorData;
      const required = ['company_name', 'npwp', 'contact_person', 'address', 'email', 'vendor_category', 'item_group', 'status'];

      if (required.some(k => d[k] === undefined || d[k] === null || String(d[k]).trim() === '')) {
        return alert('The Red Star Field is Mandatory');
      }
      required.forEach(k => formData.append(k, d[k]));
      formData.append('products', d.products || '');
      ['no_hp', 'phone', 'fax', 'note'].forEach(k => formData.append(k, (d[k] === undefined || d[k] === null || d[k] === '') ? '-' : d[k]));
      ['bank_name', 'bank_branch', 'bank_account_no', 'bank_account_name'].forEach(k => formData.append(k, d[k] || ''));
      if (d.bank_account_no && !/^[0-9 .-]+$/.test(d.bank_account_no)) {
        return alert('No. Rekening hanya boleh berisi angka');
      }

      const isAdd = context.form.title == 'Add Data';
      api = isAdd ? Api(context, vendorList.create(formData)) : Api(context, vendorList.update(d.id, formData));
      // eslint-disable-next-line no-unused-vars
      api.onSuccess(function(response) {
        context.get();
        context.getStats();
        context.form.show = false;
        context.notify(isAdd ? 'Data Berhasil di Simpan' : 'Data Berhasil di Update', 'success')
      // eslint-disable-next-line no-unused-vars
      }).onError(function(error) {
        context.notify(isAdd ? 'Data Gagal di Simpan' : 'Data Gagal di Update', 'error')
      })
      .call();
    },
    remove(id) {
      if (confirm("Are you sure you want to delete this data ?")) {
        let context = this;
        // eslint-disable-next-line no-unused-vars
        Api(context, vendorList.delete(id)).onSuccess(function(response) {
            context.get();
            context.getStats();
            context.notify('Data Successfully Deleted', 'success')
        }).call();
      }
    },
    openDetail(vendorCode) {
      let context = this;
      const fromTable = this.table.data.find(r => r.vendor_code == vendorCode);
      context.detail.vendor  = fromTable || null;
      context.detail.history = [];
      context.rating         = { ...EMPTY_RATING };
      context.getRating(vendorCode);
      Api(context, vendorList.purHistory(vendorCode)).onSuccess(function(response) {
          const rows = response.data.data || [];
          context.detail.history = rows;
          // vendor dari Top 5 bisa tidak ada di halaman tabel saat ini, ambil profil dari riwayat / index
          if (!context.detail.vendor && rows.length) {
            context.detail.vendor = rows[0];
          }
          if (context.detail.vendor) {
            context.detail.show = true;
          } else {
            Api(context, vendorList.index({ vendor_code: vendorCode })).onSuccess(function(res) {
                context.detail.vendor = res.data.data.data.data[0];
                context.detail.show   = true;
            }).call();
          }
      }).call()
    },
    changePage(page) {
      this.pagination.page = page;
      this.get();
    },
    notify(message, type) {
      const options = { position: toast.POSITION.BOTTOM_RIGHT, autoClose: 2000 };
      type == 'success' ? toast.success(message, options) : toast.error(message, options);
    },
  }
};
</script>
<style scoped>
::v-deep .modal-container {
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-y: auto;
}
::v-deep .modal-content {
  display: flex;
  flex-direction: column;
  margin: 0 1rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  background: #fff;
  width: 500px;
  margin-top: auto;
  margin-bottom: auto;
}
::v-deep .modal-form {
  width: 760px;
  max-width: 95vw;
}
::v-deep .modal-content-width {
  margin: 1rem;
  padding: 1.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  background: #fff;
  width: 1150px;
  max-width: 95vw;
  margin-top: auto;
  margin-bottom: auto;
}
.modal__title {
  font-size: 1.05rem;
  font-weight: 700;
}
.section-title {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8392ab;
  margin-bottom: 0.4rem;
}
/* KPI */
.kpi-card { transition: transform .15s ease, box-shadow .15s ease; }
.kpi-clickable { cursor: pointer; }
.kpi-clickable:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(0,0,0,.12); }
.kpi-icon {
  width: 38px; height: 38px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 15px; flex-shrink: 0;
}
/* kategori & top vendor */
.cat-row {
  display: flex; align-items: center; gap: 10px;
  padding: 4px 6px; border-radius: 6px; cursor: pointer;
}
.cat-row:hover, .cat-row.active { background: #f0f4ff; }
.cat-name { width: 130px; flex-shrink: 0; }
.cat-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; }
.cat-bar-bg { flex-grow: 1; height: 8px; background: #eef1f6; border-radius: 4px; overflow: hidden; }
.cat-bar { height: 100%; border-radius: 4px; transition: width .4s ease; }
.cat-count { width: 32px; text-align: right; }
.top-row { cursor: pointer; border-radius: 6px; }
.top-row:hover { background: #f8f9fa; }
.rank {
  width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; background: #e9ecef; color: #525f7f;
}
.rank-1 { background: #ffd600; color: #5a4a00; }
.rank-2 { background: #ced4da; }
.rank-3 { background: #e0a370; color: #fff; }
/* tabel */
.avatar-initial {
  width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 13px; font-weight: 700;
}
.avatar-lg { width: 52px; height: 52px; font-size: 18px; border-radius: 14px; }
.cat-badge { color: #fff; font-weight: 600; }
.product-chip {
  display: inline-block; font-size: 0.68rem; font-weight: 600; color: #5e72e4;
  background: #eef1ff; border-radius: 12px; padding: 2px 9px; margin: 0 4px 4px 0;
}
.chip-hit { background: #5e72e4; color: #fff; }
.history-chip {
  display: inline-block; font-size: 0.65rem; color: #1a8f5c; background: #e3f8ee;
  border-radius: 12px; padding: 2px 8px; margin: 0 4px 3px 0;
}
.action-icon { cursor: pointer; margin: 0 8px; }
.filter-chip { cursor: pointer; }
/* detail vendor */
.info-chip {
  display: inline-block; font-size: 0.7rem; font-weight: 600; color: #67748e;
  background: #f4f6fb; border-radius: 6px; padding: 2px 8px; margin: 0 4px 4px 0;
}
.stat-strip { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.stat-item { display: flex; gap: 10px; align-items: flex-start; background: #f8f9fe; border-radius: 10px; padding: 10px 14px; }
.stat-icon { color: #5e72e4; font-size: 18px; margin-top: 3px; width: 20px; text-align: center; }
.stat-label { display: block; font-size: 0.68rem; text-transform: uppercase; font-weight: 700; color: #8392ab; letter-spacing: .03em; }
.stat-value { display: block; font-size: 1.05rem; font-weight: 700; color: #344767; line-height: 1.3; }
.stat-note { display: block; font-size: 0.7rem; color: #8392ab; }
.modal-scroll-body { max-height: 58vh; overflow-y: auto; }
.contact-row { display: flex; gap: 10px; padding: 6px 0; border-bottom: 1px dashed #eef0f4; }
.contact-row:last-child { border-bottom: none; }
.contact-row i { color: #8392ab; width: 16px; text-align: center; margin-top: 3px; }
.contact-label { display: block; font-size: 0.68rem; color: #8392ab; font-weight: 600; }
.contact-value { display: block; font-size: 0.8rem; color: #344767; word-break: break-word; }
.top-item { display: flex; justify-content: space-between; gap: 8px; font-size: 0.78rem; padding: 4px 0; color: #344767; }
.top-item-count { font-weight: 700; color: #5e72e4; flex-shrink: 0; }
.empty-box { background: #f8f9fa; border-radius: 10px; }
.po-card { border: 1px solid #e9ecef; border-radius: 10px; margin-bottom: 10px; overflow: hidden; }
.po-head { display: flex; justify-content: space-between; align-items: center; background: #f4f6fb; padding: 8px 14px; }
.po-no { font-size: 0.85rem; font-weight: 700; color: #344767; margin-right: 10px; }
.po-date { font-size: 0.75rem; color: #8392ab; }
.po-total { font-size: 0.9rem; font-weight: 700; color: #344767; }
.po-meta { display: flex; flex-wrap: wrap; gap: 14px; padding: 6px 14px 0; font-size: 0.7rem; color: #8392ab; }
.po-meta i { margin-right: 3px; }
.po-item { display: flex; align-items: center; gap: 12px; padding: 8px 14px; border-top: 1px solid #f4f5f7; }
.po-item:first-of-type { border-top: none; }
.po-item-name { flex: 1 1 auto; min-width: 0; }
.po-item-calc { flex: 0 0 200px; text-align: right; }
.po-item-amount { flex: 0 0 130px; text-align: right; font-size: 0.85rem; font-weight: 700; color: #344767; }
/* rating */
.auto-score { background: #fbfcff; border: 1px dashed #d6dcf5; border-radius: 10px; padding: 10px 14px; }
.auto-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
@media (max-width: 768px) { .auto-grid { grid-template-columns: 1fr; } }
::v-deep .modal-rating { width: 620px; max-width: 95vw; }
.star-row { color: #ffb400; font-size: 14px; white-space: nowrap; }
.star-row i { margin-right: 1px; }
.star-sm { font-size: 10px; }
.rating-panel {
  display: grid; grid-template-columns: 170px 1fr 1.3fr; gap: 18px; align-items: center;
  border-radius: 12px; padding: 14px 18px; background: #f8f9fe; border-left: 5px solid #ced4da;
}
.rating-recommended { border-left-color: #2dce89; }
.rating-fair { border-left-color: #11cdef; }
.rating-evaluate { border-left-color: #fb6340; }
.rating-blacklist { border-left-color: #f5365c; background: #fff5f7; }
.rating-score { display: flex; align-items: center; gap: 10px; }
.rating-big { font-size: 2.4rem; font-weight: 800; color: #344767; line-height: 1; }
.reco-badge { display: inline-block; font-size: 0.75rem; font-weight: 700; border-radius: 6px; padding: 3px 10px; margin-top: 2px; background: #e9ecef; color: #67748e; }
.reco-sm { font-size: 0.6rem; padding: 1px 6px; }
.reco-recommended { background: #d8f7e9; color: #1a8f5c; }
.reco-fair { background: #d7f6fc; color: #0a8aa3; }
.reco-evaluate { background: #ffe8dc; color: #c2541f; }
.reco-blacklist { background: #fde1e6; color: #d0213f; }
.aspect-row { display: flex; align-items: center; gap: 10px; padding: 2px 0; }
.aspect-name { width: 160px; flex-shrink: 0; font-size: 0.75rem; color: #344767; }
.aspect-val { width: 28px; text-align: right; font-size: 0.75rem; font-weight: 700; }
.rating-item { border: 1px solid #eef0f4; border-radius: 8px; padding: 8px 12px; margin-bottom: 6px; }
.aspect-input { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 8px 0; border-bottom: 1px dashed #eef0f4; }
.star-input i { font-size: 22px; color: #ffb400; cursor: pointer; margin-left: 2px; transition: transform .1s; }
.star-input i:hover { transform: scale(1.2); }
.star-text { display: inline-block; width: 82px; font-size: 0.7rem; color: #8392ab; margin-left: 8px; }
@media (max-width: 768px) {
  .rating-panel { grid-template-columns: 1fr; }
  .stat-strip { grid-template-columns: repeat(2, 1fr); }
  .po-item { flex-wrap: wrap; }
  .po-item-calc, .po-item-amount { flex: 1 1 auto; text-align: left; }
}
.scroll-model { overflow: auto; }
.scroll-model thead th { position: sticky; top: 0; z-index: 100; background-color: #6495ED; }
.scroll { overflow: auto; max-height: 560px; }
.scroll thead th { position: sticky; top: 0; z-index: 100; background-color: #6495ED; }
/* kolom vendor tetap terlihat saat tabel digeser ke samping */
.scroll thead th:first-child { left: 0; z-index: 101; }
.scroll tbody td:first-child { position: sticky; left: 0; z-index: 50; background: #fff; box-shadow: 2px 0 4px rgba(0,0,0,.04); }
</style>
